import { Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  XMarkIcon,
  DocumentTextIcon,
  CalendarIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/solid';
import { BillingCycle } from '@views/Invoice/invoiceTypes';
import {
  getTheViewersInvoice,
  isViewedByTheShipper,
} from '@views/Invoice/invoiceHelper';
import { Button } from '../../../tailwindComponents/Button';

interface InvoiceSideDrawerProps {
  selectedBillingCycle: BillingCycle | null;
  setSelectedBillingCycle: (payload: BillingCycle | null) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isDesktop: boolean;
  viewerBusinessId?: string;
}

export function InvoiceSideDrawer({
  selectedBillingCycle,
  setSelectedBillingCycle,
  isOpen,
  setIsOpen,
  isDesktop,
  viewerBusinessId,
}: InvoiceSideDrawerProps) {
  const navigate = useNavigate();

  const onClose = () => {
    setIsOpen(false);
    setSelectedBillingCycle(null);
  };

  const getBackdropClasses = () => {
    if (!isOpen) return 'opacity-0 pointer-events-none';
    return isDesktop ? '' : 'bg-black/20';
  };

  const invoice = selectedBillingCycle
    ? getTheViewersInvoice(selectedBillingCycle, viewerBusinessId)
    : undefined;
  const isViewerTheShipper = selectedBillingCycle
    ? isViewedByTheShipper(selectedBillingCycle, viewerBusinessId)
    : false;

  const status = invoice?.status ?? 'Pending';
  const isPaid = status === 'Paid';
  const isApproved = status === 'Approved' || status === 'Finalised';

  const getStatusColor = () => {
    if (isPaid) return 'green';
    if (isApproved) return 'blue';
    return 'yellow';
  };

  const statusColor = getStatusColor();

  const getStatusBadgeClasses = () => {
    const baseClasses =
      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium';
    switch (statusColor) {
      case 'green':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'blue':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'yellow':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Calculate totals
  const subtotal =
    invoice?.lines?.reduce((sum, line) => sum + line.amount, 0) ?? 0;
  const vatAmount = subtotal * ((invoice?.vatRatePercentage ?? 20) / 100);
  const total = subtotal + vatAmount;
  const currencySymbol = invoice?.lines?.[0]?.currencySymbol ?? '£';

  return (
    <>
      {/* Backdrop - only visible on mobile */}
      <div
        className={`fixed inset-0 z-drawerBackdrop transition-opacity duration-300 ${getBackdropClasses()}`}
        onClick={onClose}
        role='presentation'
      />

      {/* Drawer */}
      <div
        className={`fixed z-drawer bg-white shadow-xl flex flex-col overflow-hidden will-change-transform transition-transform duration-300 ease-out ${
          isDesktop
            ? `right-0 top-0 h-full w-[22rem] border-l border-gray-200 ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
            : `bottom-0 left-0 right-0 max-h-[90vh] rounded-t-2xl ${
                isOpen ? 'translate-y-0' : 'translate-y-full'
              }`
        }`}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-5 py-4 bg-white border-b border-gray-100'>
          <h2 className='text-xl font-semibold text-gray-900'>Invoice</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1 rounded hover:bg-gray-100 text-gray-400 transition-colors'
            aria-label='Close'
          >
            <XMarkIcon className='w-5 h-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-5 py-4'>
          {/* Status Badge */}
          <div className='mb-6'>
            <span className={getStatusBadgeClasses()}>
              {isPaid ? (
                <CheckCircleIcon className='w-3.5 h-3.5' />
              ) : (
                <ClockIcon className='w-3.5 h-3.5' />
              )}
              {status}
            </span>
          </div>

          {/* Invoice Details Section */}
          <div className='bg-gray-50 rounded-lg p-4 mb-6'>
            <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
              Invoice Details
            </p>

            {/* Two Column Grid */}
            <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {invoice?.invoiceNumber ?? '-'}
                </p>
                <p className='text-xs text-gray-400'>Invoice Number</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {invoice?.createdAt
                    ? dayjs(invoice.createdAt).format('DD/MM/YY')
                    : '-'}
                </p>
                <p className='text-xs text-gray-400'>Created Date</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {invoice?.dueDate
                    ? dayjs(invoice.dueDate).format('DD/MM/YY')
                    : '-'}
                </p>
                <p className='text-xs text-gray-400'>Due Date</p>
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {invoice?.paidAt
                    ? dayjs(invoice.paidAt).format('DD/MM/YY')
                    : '-'}
                </p>
                <p className='text-xs text-gray-400'>Paid Date</p>
              </div>
            </div>
          </div>

          {/* Billing Period Section */}
          <div className='bg-gray-50 rounded-lg p-4 mb-6'>
            <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
              Billing Period
            </p>

            <div className='flex items-center gap-2'>
              <CalendarIcon className='w-4 h-4 text-gray-400' />
              <p className='text-sm text-gray-900'>
                {selectedBillingCycle?.startDate
                  ? dayjs(selectedBillingCycle.startDate).format('DD MMM YYYY')
                  : '-'}{' '}
                -{' '}
                {selectedBillingCycle?.endDate
                  ? dayjs(selectedBillingCycle.endDate).format('DD MMM YYYY')
                  : '-'}
              </p>
            </div>
          </div>

          {/* Parties Section */}
          <div className='bg-gray-50 rounded-lg p-4 mb-6'>
            <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
              {isViewerTheShipper ? 'Warehouse' : 'Shipper'}
            </p>

            <div className='flex items-start gap-2'>
              <BuildingOfficeIcon className='w-4 h-4 text-gray-400 mt-0.5' />
              <div>
                <p className='text-sm font-semibold text-gray-900'>
                  {isViewerTheShipper
                    ? selectedBillingCycle?.lspBusinessName
                    : selectedBillingCycle?.shipperBusinessName}
                </p>
                <p className='text-xs text-gray-500 mt-0.5'>
                  {isViewerTheShipper
                    ? selectedBillingCycle?.lspBusinessAddress
                    : selectedBillingCycle?.shipperBusinessAddress}
                </p>
              </div>
            </div>
          </div>

          {/* Amount Summary */}
          <div className='bg-blue-50 rounded-lg p-4 mb-6'>
            <p className='text-[11px] font-medium text-blue-400 uppercase tracking-wider mb-3'>
              Amount Summary
            </p>

            <div className='space-y-2'>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-600'>Subtotal</span>
                <span className='text-gray-900'>
                  {currencySymbol}
                  {subtotal.toFixed(2)}
                </span>
              </div>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-600'>
                  VAT ({invoice?.vatRatePercentage ?? 20}%)
                </span>
                <span className='text-gray-900'>
                  {currencySymbol}
                  {vatAmount.toFixed(2)}
                </span>
              </div>
              <div className='border-t border-blue-200 pt-2 mt-2'>
                <div className='flex justify-between'>
                  <span className='text-sm font-semibold text-gray-900'>
                    Total
                  </span>
                  <span className='text-lg font-bold text-blue-600'>
                    {currencySymbol}
                    {total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items Preview */}
          {invoice?.lines && invoice.lines.length > 0 && (
            <div className='mb-6'>
              <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
                Line Items ({invoice.lines.length})
              </p>

              <div className='space-y-2 max-h-40 overflow-y-auto'>
                {invoice.lines.slice(0, 5).map((line) => (
                  <div
                    key={line.id}
                    className='flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg text-sm'
                  >
                    <span className='text-gray-700 truncate flex-1 mr-2'>
                      {line.description}
                    </span>
                    <span className='text-gray-900 font-medium whitespace-nowrap'>
                      {line.currencySymbol}
                      {line.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
                {invoice.lines.length > 5 && (
                  <p className='text-xs text-gray-500 text-center py-1'>
                    +{invoice.lines.length - 5} more items
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className='px-5 py-4 bg-gray-50 border-t border-gray-200'>
          <Button
            label='View Full Invoice'
            intent='primary'
            size='sm'
            onClick={() => navigate(`/invoice/${selectedBillingCycle?.id}`)}
            startIcon={<DocumentTextIcon className='w-4 h-4' />}
            className='w-full'
          />
        </div>
      </div>
    </>
  );
}
